import { useCallback, useRef, useState } from 'react';

import { useAppDispatch, useAppSelector } from 'hooks/store';
import {
  CallScribeTimeEntryStatus,
  ICallScribeCallListItem,
  ICallScribeTimeEntryRequest,
} from 'services/callScribe/types';
import { CallScribePostingTimeEntryForSelector } from 'store/callScribe/selectors';
import {
  ThunkPostCallScribeTimeEntry,
  ThunkRejectCallScribeSuggestion,
} from 'store/callScribe/thunks';
import { alreadyPosted } from 'components/CallScribe/alreadyPosted';

export const SUBMIT_DID_NOT_COMPLETE =
  'That submit did not complete. Check the ticket before trying again.';

export const DISCARD_FAILED = 'That suggestion could not be discarded. Nothing has changed.';

type TReread = () => Promise<ICallScribeCallListItem | null | undefined>;

export interface ITimeEntrySubmit {
  submit: (body: ICallScribeTimeEntryRequest) => void;
  discard: () => void;
  error: string | undefined;
  clearError: () => void;
  posting: boolean;
  discarding: boolean;
}

export const useTimeEntrySubmit = (
  callId: number | undefined,
  reread: TReread,
): ITimeEntrySubmit => {
  const dispatch = useAppDispatch();
  const postingFor = useAppSelector(CallScribePostingTimeEntryForSelector);
  const [error, setError] = useState<string | undefined>(undefined);
  const [discarding, setDiscarding] = useState(false);
  const discardInFlight = useRef(false);

  const submit = useCallback((body: ICallScribeTimeEntryRequest): void => {
    if (typeof callId !== 'number') return;
    setError(undefined);
    dispatch(ThunkPostCallScribeTimeEntry({ callId, body }))
      .unwrap()
      .then(() => {
        void reread();
      })
      .catch(async (err: unknown) => {
        const row = await reread();
        if (alreadyPosted(err) && row?.linkStatus === CallScribeTimeEntryStatus.Posted) {
          return;
        }
        if (row?.linkStatus === CallScribeTimeEntryStatus.Failed) {
          return;
        }
        const refusal = (err as { message?: unknown })?.message;
        setError(typeof refusal === 'string' && refusal.length > 0 ? refusal : SUBMIT_DID_NOT_COMPLETE);
      });
  }, [callId, dispatch, reread]);

  const discard = useCallback((): void => {
    if (typeof callId !== 'number' || discardInFlight.current) return;
    discardInFlight.current = true;
    setDiscarding(true);
    setError(undefined);
    dispatch(ThunkRejectCallScribeSuggestion(callId))
      .unwrap()
      .then(() => {
        void reread();
      })
      .catch(() => {
        setError(DISCARD_FAILED);
        void reread();
      })
      .finally(() => {
        discardInFlight.current = false;
        setDiscarding(false);
      });
  }, [callId, dispatch, reread]);

  const clearError = useCallback((): void => { setError(undefined); }, []);

  return {
    submit,
    discard,
    error,
    clearError,
    posting: typeof callId === 'number' && postingFor === callId,
    discarding,
  };
};
